import React, { useRef, useState, useEffect } from "react";
import { Play } from "lucide-react";
import Pause from "@/assets/icons/Pause.svg";
import { audioManager } from "@/lib/audioManager";

interface CommentAudioPlayerProps {
  audioUrl: string;
  duration: number;
}

export const CommentAudioPlayer: React.FC<CommentAudioPlayerProps> = ({
  audioUrl,
  duration,
}) => {
  // console.log('[AudioPlayer] Component mounted/updated:', { audioUrl, duration });
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [actualDuration, setActualDuration] = useState(duration);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handlePlayPause = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioManager.pause(audioRef.current);
      setIsPlaying(false);
    } else {
      try {
        // This will pause other audio and play this one
        await audioManager.play(audioRef.current);
        setIsPlaying(true);
      } catch (error) {
        console.error("[AudioPlayer] Error playing audio:", error);
      }
    }
  };

  const handleWaveformClick = async (
    e: React.MouseEvent<HTMLCanvasElement>
  ) => {
    if (!audioRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * actualDuration;

    // console.log("[AudioPlayer] Waveform clicked:", {
    //   clickX: x,
    //   canvasWidth: rect.width,
    //   percentage,
    //   calculatedNewTime: newTime,
    //   propDuration: duration,
    //   actualDuration,
    //   audioDuration: audioRef.current.duration,
    //   audioCurrentTime: audioRef.current.currentTime,
    // });

    const audio = audioRef.current;

    // Seek to the new time
    audio.currentTime = newTime;
    setCurrentTime(newTime);

    // console.log("[AudioPlayer] After seek:", {
    //   audioCurrentTime: audio.currentTime,
    // });

    // Wait a bit for the seek to complete, then play
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Always start playing after navigation
    try {
      await audioManager.play(audio);
      setIsPlaying(true);
    } catch (error) {
      console.error("[AudioPlayer] Error playing audio:", error);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Generate waveform data
  useEffect(() => {
    let cancelled = false;

    const generateWaveform = async () => {
      try {
        const response = await fetch(audioUrl);
        if (cancelled) return;

        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;

        const audioContext = new AudioContext();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        if (cancelled) {
          await audioContext.close();
          return;
        }

        const rawData = audioBuffer.getChannelData(0);
        const samples = 100; // Number of bars
        const blockSize = Math.floor(rawData.length / samples);
        const filteredData = [];

        for (let i = 0; i < samples; i++) {
          let blockStart = blockSize * i;
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(rawData[blockStart + j]);
          }
          filteredData.push(sum / blockSize);
        }

        if (!cancelled) {
          setWaveformData(filteredData);
        }
        await audioContext.close();
      } catch (error) {
        if (!cancelled) {
          // Generate a placeholder waveform with random values
          const placeholderData = Array.from(
            { length: 100 },
            () => Math.random() * 0.5 + 0.3
          );
          setWaveformData(placeholderData);
        }
      }
    };

    generateWaveform();

    return () => {
      cancelled = true;
    };
  }, [audioUrl]);

  // Draw waveform on canvas
  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas || waveformData.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const canvasHeight = 24;

      // Update canvas size
      canvas.width = rect.width * dpr;
      canvas.height = canvasHeight * dpr;
      ctx.scale(dpr, dpr);

      const barWidth = 2;
      const barGap = 2;

      // Calculate how many bars can fit in the available width
      const maxBars = Math.floor(rect.width / (barWidth + barGap));
      const barCount = Math.min(waveformData.length, maxBars);

      // Calculate actual spacing to fill the entire width
      const totalBarWidth = barCount * barWidth;
      const totalGapWidth = rect.width - totalBarWidth;
      const actualGap = barCount > 1 ? totalGapWidth / (barCount - 1) : 0;

      ctx.clearRect(0, 0, rect.width, canvasHeight);

      const progress = currentTime / actualDuration;

      // Normalize waveform data to make it more visible
      const max = Math.max(...waveformData);
      const multiplier = max > 0 ? 1 / max : 1;

      for (let i = 0; i < barCount; i++) {
        // Sample from waveformData proportionally
        const dataIndex = Math.floor((i / barCount) * waveformData.length);
        const normalizedValue = waveformData[dataIndex] * multiplier;
        const barHeight = Math.max(normalizedValue * canvasHeight * 0.8, 2);
        const x = i * (barWidth + actualGap);
        const y = (canvasHeight - barHeight) / 2;

        // Color bars differently based on progress
        const barProgress = i / barCount;
        ctx.fillStyle = barProgress <= progress ? "#ffffff" : "#ffffff40";
        ctx.fillRect(x, y, barWidth, barHeight);
      }
    };

    draw();

    // Set up ResizeObserver to redraw when container size changes
    const resizeObserver = new ResizeObserver(() => {
      draw();
    });

    resizeObserver.observe(canvas);

    return () => {
      resizeObserver.disconnect();
    };
  }, [waveformData, currentTime, actualDuration, audioUrl]);

  // Set up event listeners - only when audio URL changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      // console.log("[AudioPlayer] Audio ended");
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };

    const handlePlay = () => {
      // console.log("[AudioPlayer] Audio playing");
      setIsPlaying(true);
    };

    const handlePause = () => {
      // console.log("[AudioPlayer] Audio paused");
      setIsPlaying(false);
    };

    const handleError = () => {
      console.error("[AudioPlayer] Audio error:", audio.error);
    };

    const handleLoadedMetadata = () => {
      // console.log("[AudioPlayer] Metadata loaded:", {
      //   audioDuration: audio.duration,
      //   propDuration: duration,
      //   audioUrl,
      // });
      // Use the actual audio duration instead of the prop
      if (!isNaN(audio.duration) && isFinite(audio.duration)) {
        setActualDuration(audio.duration);
      }
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [audioUrl, duration]);

  return (
    <div className="flex items-center gap-3 mt-2 p-1 pr-3 bg-gray-300 rounded-[14px]">
      <button
        type="button"
        className="outline-none border-0 bg-white/10 transition-all hover:bg-white/20 rounded-[10px] w-[40px] h-[32px] cursor-pointer flex items-center justify-center flex-shrink-0"
        onClick={handlePlayPause}
      >
        {isPlaying ? (
          <img src={Pause} />
        ) : (
          // <Pause size={16} color="#ffffff" />
          <Play size={16} color="#ffffff" fill="#ffffff" />
        )}
      </button>
      <canvas
        ref={canvasRef}
        className="flex-1 min-w-0 cursor-pointer block"
        style={{
          height: "24px",
          maxHeight: "24px",
          minHeight: "24px",
          width: "100%",
        }}
        onClick={handleWaveformClick}
      />
      <span className="text-xs text-white/50 min-w-[35px] font-medium flex-shrink-0 text-center">
        {isPlaying || currentTime > 0
          ? formatTime(currentTime)
          : formatTime(actualDuration)}
      </span>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        className="hidden"
      />
    </div>
  );
};
